import { useEffect, useState, useCallback, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import { ClipboardList, Map, Wallet, Settings } from 'lucide-react';
import { APP_ROUTES, isAdminUser } from '../../utils/auth';
import { subscribePush } from '../../utils/webpush';
import Sidebar from '../../components/Sidebar';

// Chargement différé — chaque page n'est téléchargée que lors de la première visite
const Commandes     = lazy(() => import('../Commandes'));
const Confirmations = lazy(() => import('../Confirmations'));
const ClosingQueue  = lazy(() => import('../ClosingQueue'));
const GPS           = lazy(() => import('../GPS'));
const Users         = lazy(() => import('../Users'));
const Equipe        = lazy(() => import('../Equipe'));
const Versements    = lazy(() => import('../Versements'));
const Expedition    = lazy(() => import('../Expedition'));
const AllOrders     = lazy(() => import('../AllOrders'));
const Traitees      = lazy(() => import('../Traitees'));
const Retours       = lazy(() => import('../Retours'));
const Bilans        = lazy(() => import('../Bilans'));
const Finances      = lazy(() => import('../Finances'));
const Planning      = lazy(() => import('../Planning'));
const DriverBilan   = lazy(() => import('../DriverBilan'));
const Audit         = lazy(() => import('../Audit'));
const Presences     = lazy(() => import('../Presences'));
const Reglement     = lazy(() => import('../Reglement'));
const Candidatures  = lazy(() => import('../Candidatures'));

// Squelette affiché pendant le chargement d'une page
const PageSkeleton = () => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
    {[0, 1, 2, 3].map(i => (
      <div key={i} style={{ height: '72px', background: 'white', borderRadius: '14px', border: '1px solid #eef0f6', opacity: 0.35 + i * 0.18 }} />
    ))}
  </div>
);

const ADMIN_LOGIN_PATH = APP_ROUTES.landing;

export default function AdminApp() {
  const navigate = useNavigate();
  const [activeNav, setActiveNav] = useState(() => {
    // Deep-link depuis une notification push (ex: /dashboard#retours)
    const hash = window.location.hash.replace('#', '');
    if (hash) {
      window.history.replaceState(null, '', window.location.pathname);
      return hash;
    }
    return localStorage.getItem('wakaana_nav') || 'commandes';
  });
  const [pendingCount, setPendingCount] = useState(0);
  const [failedCount, setFailedCount]   = useState(0);
  const [closingCount, setClosingCount] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [contentVisible, setContentVisible] = useState(true);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false);
  const [fromCommandes, setFromCommandes] = useState(false);

  useEffect(() => {
    let mounted = true;
    let authSubscription = null;
    const redirectToLogin = () => {
      if (mounted) navigate(ADMIN_LOGIN_PATH, { replace: true });
    };

    const checkSession = async () => {
      const { data } = await supabase.auth.getSession();
      const user = data.session?.user;
      if (!mounted) return;
      if (!user || !isAdminUser(user)) {
        if (user) await supabase.auth.signOut();
        redirectToLogin();
        return;
      }
      setAuthReady(true);
      subscribePush(supabase, { role: 'admin' });
    };

    checkSession();

    const { data: listener } = supabase.auth.onAuthStateChange(async (event, session) => {
      const user = session?.user;
      if (!user || !isAdminUser(user)) {
        if (event !== 'SIGNED_OUT' && user) await supabase.auth.signOut();
        redirectToLogin();
        return;
      }
      if (mounted) {
        setAuthReady(true);
        subscribePush(supabase, { role: 'admin' });
      }
    });
    authSubscription = listener?.subscription || null;

    return () => {
      mounted = false;
      if (authSubscription) authSubscription.unsubscribe();
    };
  }, [navigate]);

  const fetchPending = useCallback(async () => {
    const [pendingRes, failedRes, closingRes] = await Promise.all([
      supabase.from('deliveries').select('id', { count: 'exact', head: true }).eq('confirmation_status', 'new'),
      supabase.from('deliveries').select('id', { count: 'exact', head: true }).eq('status', 'failed').neq('confirmation_status', 'cancelled').not('expedition', 'eq', true),
      supabase.from('deliveries').select('id', { count: 'exact', head: true }).in('confirmation_status', ['new', 'recall']),
    ]);
    if (!pendingRes.error) setPendingCount(pendingRes.count || 0);
    if (!failedRes.error)  setFailedCount(failedRes.count  || 0);
    if (!closingRes.error) setClosingCount(closingRes.count || 0);
    setPendingLoading(false);
  }, []);

  useEffect(() => {
    let channel = null;
    let refreshTimer = null;
    fetchPending();

    channel = supabase
      .channel('realtime:pending-count')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'deliveries' }, () => {
        if (refreshTimer) clearTimeout(refreshTimer);
        refreshTimer = setTimeout(() => fetchPending(), 350);
      })
      .subscribe();

    return () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      if (channel) supabase.removeChannel(channel);
    };
  }, [fetchPending]);

  useEffect(() => {
    setContentVisible(false);
    const id = requestAnimationFrame(() => setContentVisible(true));
    return () => cancelAnimationFrame(id);
  }, [activeNav]);

  const handleNavigate = (nav) => {
    if (nav !== 'livraisons') setFromCommandes(false);
    setActiveNav(nav);
    localStorage.setItem('wakaana_nav', nav);
  };

  const handleLogout = () => {
    localStorage.removeItem('wakaana_nav');
    supabase.auth.signOut().finally(() => navigate(ADMIN_LOGIN_PATH, { replace: true }));
  };

  const renderPage = () => {
    if (activeNav === 'commandes')     return <Commandes onShowAllOrders={() => { setFromCommandes(true); handleNavigate('livraisons'); }} onNavigateTo={handleNavigate} />;
    if (activeNav === 'fermeture')     return <ClosingQueue />;
    if (activeNav === 'confirmations') return <Confirmations refreshKey={refreshKey} />;
    if (activeNav === 'livraisons')    return <AllOrders onBack={fromCommandes ? () => { setFromCommandes(false); handleNavigate('commandes'); } : null} />;
    if (activeNav === 'users')         return <Users />;
    if (activeNav === 'carte')         return <GPS />;
    if (activeNav === 'equipe')        return <Equipe />;
    if (activeNav === 'versements')    return <Versements />;
    if (activeNav === 'traitees')      return <Traitees />;
    if (activeNav === 'retours')       return <Retours />;
    if (activeNav === 'expedition')    return <Expedition />;
    if (activeNav === 'bilans')        return <Bilans />;
    if (activeNav === 'finances')      return <Finances />;
    if (activeNav === 'planning')      return <Planning />;
    if (activeNav === 'stock')         return <DriverBilan />;
    if (activeNav === 'presences')     return <Presences />;
    if (activeNav === 'audit')         return <Audit />;
    if (activeNav === 'reglement')     return <Reglement role="admin" />;
    if (activeNav === 'candidatures')  return <Candidatures />;
    return (
      <div style={{ background: 'white', borderRadius: '16px', padding: '56px', textAlign: 'center', border: '1px solid #eef0f6', boxShadow: '0 2px 8px rgba(19,40,72,0.05)' }}>
        <div style={{ width: '56px', height: '56px', borderRadius: '16px', background: 'linear-gradient(135deg, #f1f5f9, #e2e8f0)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
          <Settings size={24} color="#94a3b8" />
        </div>
        <div style={{ fontWeight: 700, fontSize: '16px', color: '#173b78', marginBottom: '6px' }}>En développement</div>
        <p style={{ fontSize: '13px', color: '#9ca3af' }}>Cette section arrive dans la prochaine version.</p>
      </div>
    );
  };

  // Onglets bottom nav mobile
  const bottomTabs = [
    { id: 'commandes',  label: 'Commandes',  Icon: ClipboardList, badge: pendingLoading ? 0 : pendingCount },
    { id: 'carte',      label: 'Carte',      Icon: Map },
    { id: 'versements', label: 'Versements', Icon: Wallet },
  ];

  return (
    <div style={{ display: 'flex', height: '100dvh', overflow: 'hidden', background: '#f0f3f8' }}>
      {!authReady ? (
        <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <div style={{ background: 'white', border: '1px solid #eef0f6', borderRadius: '16px', padding: '20px 24px', fontSize: '13px', color: '#64748b', fontWeight: 600 }}>
            Vérification de la session administrateur...
          </div>
        </main>
      ) : (
        <>
      <Sidebar
        activeNav={activeNav}
        onNavigate={handleNavigate}
        onLogout={handleLogout}
        pendingCount={pendingCount}
        failedCount={failedCount}
        closingCount={closingCount}
        onRefresh={() => setRefreshKey(k => k + 1)}
      />
      <main style={{ flex: 1, padding: '16px', marginTop: '64px', maxWidth: '100vw', overflowX: 'hidden', overflowY: 'auto' }}
        className="md-main admin-mobile-pad">
        <div style={{
          opacity: contentVisible ? 1 : 0,
          transition: 'opacity 0.16s ease',
        }}>
          <Suspense fallback={<PageSkeleton />}>
            {renderPage()}
          </Suspense>
        </div>
      </main>

      {/* ── Bottom nav mobile (masqué sur desktop) ── */}
      <div className="admin-bottom-nav" style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 999,
        background: 'white',
        borderTop: '1px solid #eef0f6',
        boxShadow: '0 -4px 24px rgba(19,40,72,0.08)',
        display: 'flex', padding: '0 4px',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}>
        {bottomTabs.map(({ id, label, Icon, badge }) => {
          const active = activeNav === id;
          return (
            <button key={id} onClick={() => handleNavigate(id)} style={{
              flex: 1, padding: '10px 4px 8px', border: 'none', background: 'none', cursor: 'pointer',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '3px',
              position: 'relative', transition: 'all 0.15s',
            }}>
              {active && (
                <div style={{ position: 'absolute', top: 0, left: '20%', right: '20%', height: '2px', borderRadius: '0 0 3px 3px', background: 'linear-gradient(90deg, #173b78, #2a66b8)' }} />
              )}
              <div style={{
                width: '34px', height: '34px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: active ? 'linear-gradient(135deg, #173b78, #2a66b8)' : 'transparent',
                transition: 'all 0.15s',
                boxShadow: active ? '0 4px 12px rgba(23,59,120,0.25)' : 'none',
              }}>
                <Icon size={16} color={active ? 'white' : '#94a3b8'} />
              </div>
              <span style={{ fontSize: '10px', fontWeight: active ? 700 : 500, color: active ? '#173b78' : '#94a3b8', transition: 'all 0.15s' }}>
                {label}
              </span>
              {badge > 0 && (
                <span style={{ position: 'absolute', top: '6px', right: 'calc(50% - 22px)', minWidth: '16px', height: '16px', borderRadius: '8px', background: '#c53a2d', color: 'white', fontSize: '9px', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px', boxShadow: '0 2px 6px rgba(197,58,45,0.4)' }}>
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
        </>
      )}
    </div>
  );
}
